import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { BollettaViewerDialog } from './BollettaViewerDialog';
import { BarChart3, Eye, Flame, Globe, Loader2, Mail, Phone, RefreshCw, Sparkles, Wifi, Zap, ExternalLink } from 'lucide-react';

const servIcon = (s) => (s === 'gas' ? Flame : s === 'fibra' || s === 'internet' ? Wifi : Zap);
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-');

const BollettaRow = ({ b, onView, onAnalizza, analizzando }) => (
  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-lg border border-slate-200 bg-white p-2.5" data-testid={`leadweb-bolletta-${b.id}`}>
    <div className="min-w-0 text-sm">
      <p className="font-medium text-slate-900 truncate">
        {(b.fornitura || '').toUpperCase()} · {b.fornitore || 'Fornitore N/D'} · {b.importo != null ? `${Number(b.importo).toFixed(2)} €` : 'Importo N/D'}
      </p>
      <p className="text-[11px] text-slate-500">
        {b.periodo_fatturazione || (b.periodo_from ? `${b.periodo_from} - ${b.periodo_to || '?'}` : 'Periodo N/D')}
        {b.costo_unitario ? ` · ${b.costo_unitario} ${b.fornitura === 'gas' ? '€/Smc' : '€/kWh'}` : ''}
        {b.quota_fissa_mensile ? ` · fisso ${Number(b.quota_fissa_mensile).toFixed(2)} €/mese` : ''}
      </p>
    </div>
    {b.file_path && (
      <div className="flex flex-wrap gap-1 shrink-0">
        <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={onView} data-testid={`leadweb-vedi-${b.id}`}>
          <Eye className="w-3 h-3 mr-1" />Vedi
        </Button>
        <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={onAnalizza} disabled={analizzando} data-testid={`leadweb-analizza-${b.id}`}>
          {analizzando ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Sparkles className="w-3 h-3 mr-1" />Analizza AI</>}
        </Button>
      </div>
    )}
  </div>
);

const LeadWebCard = ({ lead, onPreventivo, preparando, onAnalizza, analizzandoId, onView, onOpen }) => {
  const ld = lead.landing_data || {};
  const serv = (ld.servizio || lead.interesse || 'luce').toLowerCase();
  const Icon = servIcon(serv);
  const dati = [
    ld.cap || lead.cap ? `CAP ${ld.cap || lead.cap}` : null,
    ld.comune || lead.citta,
    ld.consumo_annuo_kwh ? `${ld.consumo_annuo_kwh} kWh/anno` : null,
    ld.consumo_annuo_smc ? `${ld.consumo_annuo_smc} Smc/anno` : null,
    ld.operatore_attuale ? `Operatore: ${ld.operatore_attuale}` : null,
    ld.offerta_scelta || lead.offerta_scelta ? `Scelta: ${ld.offerta_scelta || lead.offerta_scelta}` : null,
  ].filter(Boolean);
  return (
    <Card className="border-slate-200 min-w-0 overflow-hidden" data-testid={`leadweb-card-${lead.id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-sky-100 text-sky-600 grid place-items-center shrink-0"><Icon className="w-5 h-5" /></div>
            <div className="min-w-0">
              <p className="font-semibold text-slate-900 truncate">{lead.nome} {lead.cognome}</p>
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-slate-500 mt-0.5">
                {lead.telefono && <a href={`tel:${lead.telefono}`} className="inline-flex items-center gap-1 hover:text-sky-600"><Phone className="w-3 h-3" />{lead.telefono}</a>}
                {lead.email && <span className="inline-flex items-center gap-1 min-w-0 break-all"><Mail className="w-3 h-3" />{lead.email}</span>}
              </div>
            </div>
          </div>
          <div className="text-right shrink-0">
            <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase ${lead.origine === 'portale' ? 'bg-emerald-100 text-emerald-700' : 'bg-sky-100 text-sky-700'}`}>
              {lead.origine === 'portale' ? 'Portale' : 'Sito web'}
            </span>
            <p className="text-[11px] text-slate-500 mt-1">{fmtDate(lead.created_at)}</p>
          </div>
        </div>
        {dati.length > 0 && <p className="text-xs text-slate-600">{dati.join(' · ')}</p>}
        {lead.bollette_portale?.length > 0 && (
          <div className="space-y-2">
            {lead.bollette_portale.map((b) => (
              <BollettaRow key={b.id} b={b} onView={() => onView(lead.id, b)} onAnalizza={() => onAnalizza(lead.id, b.id)} analizzando={analizzandoId === b.id} />
            ))}
          </div>
        )}
        <div className="flex flex-wrap gap-2 pt-1">
          <Button size="sm" className="bg-sky-600 hover:bg-sky-700 text-white" onClick={() => onPreventivo(lead.id)} disabled={preparando} data-testid={`leadweb-preventivo-${lead.id}`}>
            {preparando ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <BarChart3 className="w-4 h-4 mr-1.5" />}Fai preventivo
          </Button>
          <Button size="sm" variant="outline" onClick={() => onOpen(lead.id)} data-testid={`leadweb-apri-${lead.id}`}>
            <ExternalLink className="w-4 h-4 mr-1.5" />Apri lead
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export const LeadsWebTab = ({ leads, loading, onRefresh, onPreventivo, preparandoId, onAnalizza, analizzandoId, onOpen }) => {
  const [viewer, setViewer] = useState(null);
  return (
    <div className="space-y-4 min-w-0" data-testid="leadsweb-tab">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-slate-500 flex items-center gap-2"><Globe className="w-4 h-4" />Richieste arrivate dal sito e dall'Area Clienti</p>
        <Button size="sm" variant="outline" onClick={onRefresh} disabled={loading} data-testid="leadsweb-refresh">
          <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />Aggiorna
        </Button>
      </div>
      {loading && leads.length === 0 && <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>}
      {!loading && leads.length === 0 && (
        <Card><CardContent className="py-12 text-center text-sm text-slate-500" data-testid="leadsweb-empty">Nessuna richiesta dal web per ora.</CardContent></Card>
      )}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 min-w-0">
        {leads.map((l) => (
          <LeadWebCard key={l.id} lead={l} onPreventivo={onPreventivo} preparando={preparandoId === l.id}
            onAnalizza={onAnalizza} analizzandoId={analizzandoId} onView={(leadId, b) => setViewer({ leadId, b })} onOpen={onOpen} />
        ))}
      </div>
      <BollettaViewerDialog leadId={viewer?.leadId} bolletta={viewer?.b || null} onClose={() => setViewer(null)} />
    </div>
  );
};
